<script>
   /*************************************************************/
   /* PrepWidget                                                */
   /* -------------------------                                 */
   /* preprocessing widget for any app                          */
   /*************************************************************/

   import { Button, ButtonCancel, ButtonSettings, Container, Spinner } from 'svelte-controls-basic';
   import { prep_fit } from '@mdatools-apps/wasm/prep.js';
   import { applyPrep } from '../../methods/prep.js';


   import Modal from '../Modal.svelte';
   import ModelLoader from './ModelLoader.svelte';
   import PrepModal from './PrepModal.svelte';
   import PrepInfo from '../PrepInfo.svelte';

   // WASM promise and session object from parent app
   let {
      WASM,
      rawdata,
      outliers = [],                   // nimca passes none: its set is pruned already
      prepType = $bindable(),
      prepobj = $bindable(),
      prepdata = $bindable(),
      prepmodel = $bindable(),
      prepmeta = $bindable(),
   } = $props();

   // internal state of the widget
   let dialogOpen = $state(false);

   let errMsg = $state('');

   // the dialog edits these, not the session fields: a chain edited in place would be stored
   // beside a model of another chain by any backup written while the dialog is open. filled by
   // 'openDialog', published by 'closeDialog'
   let draftObj = $state([]);
   let draftMeta = $state(null);

   // every asynchronous write is stamped with the run which asked for it, and only the newest run
   // may write. 'WASM()' builds a fresh module per call, so two runs in flight are unrelated
   // promises with no ordering between them. one counter for both functions: fitting produces the
   // model that applying consumes, so a new fit must retire a pending apply. plain 'let', never
   // '$state' - it is written from inside the effects below, and a reactive write there would
   // re-trigger them - and never module scope, which every instance would share
   let run = 0;


   /****************************/
   /* Helper functions         */
   /****************************/

   // fit preprocessing model using prepobject directives.
   async function prepFit(rawdata, prepobj, outliers) {

      // the bump sits above the validity guard: an early return still ends the premise of
      // whatever is in flight, and leaving it unretired lets a superseded run write
      const myRun = ++run;
      errMsg = '';

      if (!prepobj || !rawdata || prepobj.length === 0) return;

      WASM()
         .then(Module => {
            if (myRun !== run) return;
            const m = prep_fit(Module, rawdata.values, rawdata.nresp, prepobj, false, outliers);
            // a copy cut to the chain: the live array is the session's, and its slots past
            // the chain keep the text of methods removed before the fit. a 'prepmeta' that is
            // not an array, held under a chain only by a damaged session, gives empty texts
            m.info = Array.isArray(prepmeta)
               ? $state.snapshot(prepmeta).slice(0, m.ml.length)
               : new Array(m.ml.length).fill('');
            // the data of the model being replaced goes in the same batch: the apply effect runs
            // only while there is none, so data of an earlier model would otherwise stay under
            // the new one
            prepdata = null;
            prepmodel = m;
         })
         .catch(e =>{
            console.error(e);
            if (myRun !== run) return;

            // a failed fit produced no model, so preprocessed data from an earlier one has to go
            // as well - otherwise the model says none while the data says preprocessed. clearing
            // 'prepType' returns the widget to the chooser, but only when the dialog is closed:
            // a fit can fail while the user is still editing the chain
            prepmodel = null;
            prepdata = null;
            if (!dialogOpen) prepType = null;
            errMsg = e instanceof Error ? e.message : String(e);
         });
   }

   // applies current preprocessing sequence to full dataset.
   async function prepApply(data, model) {

      const myRun = ++run;
      errMsg = '';

      // no model - no preprocessed data
      if (!model || typeof model !== 'object') {
         prepdata = null;
         return;
      }

      // preprocessing skipped - the data itself, by reference, not a copy. the embedders bind this
      // prop to a session key of their own beside the raw set's, so a backup writes the set under
      // each key in full and a restore parses one object per key; until then a field written into
      // the set under one key shows under all of them, as nimca's token does. a clone would add a
      // copy in memory and keep such a field to its key
      if (Object.keys(model).length === 0) {
         prepdata = data;
         return;
      }

      WASM()
         .then(Module => {
            if (myRun !== run) return;
            // the snapshot leaves the matrix out: it cloned it in full for 'applyPrep' to drop
            const { values, ...meta } = data;
            prepdata = applyPrep(Module, { values, ...$state.snapshot(meta) }, model);
         })
         .catch(e =>{
            console.error(e);
            if (myRun !== run) return;
            prepdata = null;

            // a created model which can not be applied leaves the widget with no usable result and
            // no matching branch in the template. a model loaded from a file stays visible in
            // 'ModelLoader' instead, where it can be inspected or reset without picking the file
            // again - a failed application does not prove the model itself is wrong
            if (prepType !== 'file') {
               prepmodel = null;
               if (!dialogOpen) prepType = null;
            }
            errMsg = e instanceof Error ? e.message : String(e);
         });
   }

   // reset preprocessing settings
   function reset() {

      // reset ends the premise of whatever is in flight. the apply effect's teardown covers this
      // only while the write actually changes 'prepmodel' - assigning null over null short-circuits
      // in 'internal_set' and fires nothing - so the run is retired here
      run++;
      errMsg = '';

      prepType = null;
      prepdata = null;
      prepmodel = null;
      prepobj = [];
   }

   // Finalize: closes the dialog and commits the draft. the fit effect below is the one launcher: a
   // published chain with a nulled model fires it once, an unchanged chain under a present
   // model fires nothing. a chain without a model is republished as well, whatever nulled the
   // model, so Finalize never leaves a non-empty chain unfitted
   function closeDialog() {
      dialogOpen = false;

      const same = JSON.stringify(draftObj) === JSON.stringify($state.snapshot(prepobj));
      if (!(same && prepmodel)) {
         // same reasoning as in 'reset' - the null writes below may change nothing, so the run
         // is retired explicitly rather than through the effect teardown
         run++;
         prepobj = $state.snapshot(draftObj);
         prepmeta = $state.snapshot(draftMeta);
         prepmodel = null;
         prepdata = null;
      }

      if (prepobj && prepobj.length > 0) {
         prepType = 'create';
      } else {
         // same reasoning as in 'reset' - the null writes below may change nothing, so the run
         // is retired explicitly rather than through the effect teardown
         run++;
         prepType = null;
         prepmodel = null;
         prepdata = null;
      }
   }

   // the close button and Escape: the draft is dropped, the session fields stay as they were. a
   // created chain whose model is gone (nulled behind the modal) is republished, so it is refitted
   function cancelDialog() {
      dialogOpen = false;
      if (prepType === 'create' && prepobj?.length > 0 && !prepmodel) {
         prepobj = $state.snapshot(prepobj);
      }
   }

   // opens the dialog on a copy of the chain and its settings text. the session fields stay as
   // they are until Finalize commits the copy
   function openDialog() {
      draftObj = $state.snapshot(prepobj);
      draftMeta = $state.snapshot(prepmeta);
      dialogOpen = true;
   }

   // skips preprocessing
   function skip() {
      prepType = 'skip';
      prepmodel = {};
   }

   /****************************/
   /* Effects                  */
   /****************************/

   // apply while there is a model and no preprocessed data. a model or raw data never arrives
   // over data of another: the paths which replace them clear it in the same batch - the
   // dialog committing, reset, a fit landing or failing, a failed apply, the embedding tabs'
   // handlers and effects - and 'skip' and a model file start from the chooser, which those
   // paths leave with none. so a present result is a current one, and a restored session is
   // trusted the same way: a backup written during an apply holds null and is applied again,
   // one written after it is not, which used to cost a module instantiation, the apply and
   // whatever the tab does with a new set on every reload
   $effect(() => {
      if (prepmodel && rawdata && !prepdata) {
         prepApply(rawdata, prepmodel);
      }

      // the teardown belongs here and not on the fit effect below: within one flush the order is
      // apply-teardown, apply-body, fit-teardown, so a bump there would cancel the apply this
      // effect had just launched, on every successful fit
      return () => { run++; };
   });

   // fit a created chain while it has no model. nothing here retires a model: the dialog nulls
   // it on a changed chain and the tabs null it when the outlier list changes, so this effect is
   // the one launcher of a fit
   $effect(() => {
      if (prepType === 'create' && prepobj && !prepmodel) {
         prepFit(rawdata, prepobj, outliers);
      }
   });

   // a skipped session whose model is gone as well as its data: gls's outlier handlers null the
   // model whatever the mode, and a stored session can hold that shape. with the empty model
   // still there the apply effect above assigns the data, by reference, before this runs
   $effect(() => {
      if (prepType === 'skip' && prepdata === null) {
         prepmodel = {};
         prepobj = [];
         prepdata = rawdata;     // by reference, as in 'prepApply'
      }
   });

</script>

<div class="prep-widget">
   <!-- no control of the widget while the dialog is open, so none can reopen it over the draft
        or write the fields under it (the page behind a modal dialog is inert as well) -->
   {#if !dialogOpen}
   {#if prepmodel && prepdata && prepmodel.ml}
   <Container name="info">
      <p class="info-text">
         <span class="label">Preprocessing: </span>
         <PrepInfo {prepmodel} />
      </p>

      {#if prepType === 'create'}
         <ButtonSettings onclick={openDialog} />
      {/if}
      <ButtonCancel onclick={reset} />

   </Container>
   {:else if prepType === 'skip'}
   <Container name="prep-info">
      <p class="info-text">
         <span class="label">Preprocessing: </span> skipped
      </p>
      <div>
      <ButtonCancel onclick={reset} />
      </div>
   </Container>
   {:else if !prepType}
   <Container label="Preprocessing" name="prep-container">
      <Button text="Skip" onclick={skip}/>
      <Button text="Load" onclick={() => prepType = 'file'}/>
      <Button text="Create" onclick={openDialog}/>
   </Container>
   {:else if prepType === 'file'}
      <!-- the cancel is the only way back to the chooser before a model is loaded -->
      <Container name="prep-info">
         <p class="info-text"><strong>Preprocessing model</strong></p>
         <div>
         <ButtonCancel onclick={reset} />
         </div>
      </Container>
      <ModelLoader bind:model={prepmodel} />
   {:else}
      <!-- a created chain between the dialog's close and its result (the fit, then the apply),
           and any state the branches above do not name: the row vanished here until the result
           landed, for good when none came -->
      <Container name="prep-info">
         <p class="info-text"><span class="label">Preprocessing: </span></p>
         <Spinner />
         <div>
         <ButtonCancel onclick={reset} />
         </div>
      </Container>
   {/if}
   {/if}
</div>

{#if dialogOpen}
<Modal closeModal={cancelDialog} isDark={true}>
   <PrepModal
      {WASM}
      {rawdata}
      bind:prepobj={draftObj}
      bind:meta={draftMeta}
      finalize={closeDialog}
   />
</Modal>
{/if}


<!-- error message -->
{#if errMsg}
<Container status="error">
   <p>{errMsg}</p>
</Container>
{/if}

<style>
   .info-text > .label {
      color: #a0a0a0;
   }

   /* the .control-element part is needed to outweigh the `margin: 0` that
      svelte-controls-basic sets on Button — without it the two selectors tie on
      specificity and the winner depends on CSS order, which is not stable */
   :global(.control-element.prep-container > button){
      margin-left: 0.25em;
   }

</style>